import { inject } from 'vue';
import { IKWorkloadsPageData } from '@/views/workloads/workloads.injectionKeys';
import { useWorkloadsTableConstants, WORKLOADS_DURATION_UNITS } from '@/views/workloads/workloads.const';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useOsService } from '@/services/OsService/OsService';
import { TableColumns } from '@/views/workloads/workloads-table.types';

export const useWorkloadsTableUtils = () => {
  const osService = useOsService();
  const workloadsConstants = useWorkloadsTableConstants();
  const workloadsPageData = inject(IKWorkloadsPageData);
  const secondsToHoursMinutesAndSeconds = (totalSeconds: number) => {
    if (totalSeconds === 0) {
      return '<1s';
    }
    const hours = Math.floor(totalSeconds / (60 * 60));
    const divisor_for_minutes = totalSeconds % (60 * 60);
    const minutes = Math.floor(divisor_for_minutes / 60);
    const divisor_for_seconds = divisor_for_minutes % 60;
    const seconds = Math.ceil(divisor_for_seconds);

    const res = {
      h: hours > 0 ? `${hours}h` : '',
      m: minutes > 0 ? `${minutes}m` : '',
      s: seconds > 0 ? `${seconds}s` : '',
    };

    return `${res.h} ${res.m} ${res.s}`;
  };

  const convertValueToCurrentUnitSelection = (fromUnit: WORKLOADS_DURATION_UNITS, toUnit: WORKLOADS_DURATION_UNITS, valueToConvert: number, roundUp: boolean = true) => {
    let resValue = valueToConvert;

    if (
      (fromUnit === WORKLOADS_DURATION_UNITS.Hours && toUnit === WORKLOADS_DURATION_UNITS.Minutes) ||
      (fromUnit === WORKLOADS_DURATION_UNITS.Minutes && toUnit === WORKLOADS_DURATION_UNITS.Seconds)
    ) {
      resValue *= 60;
    }
    if (fromUnit === WORKLOADS_DURATION_UNITS.Hours && toUnit === WORKLOADS_DURATION_UNITS.Seconds) {
      resValue *= 3600;
    }
    if (
      (fromUnit === WORKLOADS_DURATION_UNITS.Minutes && toUnit === WORKLOADS_DURATION_UNITS.Hours) ||
      (fromUnit === WORKLOADS_DURATION_UNITS.Seconds && toUnit === WORKLOADS_DURATION_UNITS.Minutes)
    ) {
      resValue = roundUp ? Math.ceil(resValue / 60) : Math.floor(resValue / 60);
    }
    if (fromUnit === WORKLOADS_DURATION_UNITS.Seconds && toUnit === WORKLOADS_DURATION_UNITS.Hours) {
      resValue = roundUp ? Math.ceil(resValue / 3600) : Math.floor(resValue / 3600);
    }
    return resValue;
  };

  const handlePageResize = async (pageSize: number) => {
    if (!workloadsPageData?.filter) {
      return;
    }
    workloadsPageData.filter.setPageSize(pageSize);
  };

  const shouldShowCloudColumns = () => (osService.isLinux.value || osService.isWindows.value) && workloadsPageData?.containsCloudRelevantData.value;

  const shouldShowCacheColumns = () => osService.isWindows.value && workloadsPageData?.containsBuildCacheData.value;

  const shouldShowInitiatorTypeColumn = () => osService.isWindows.value && workloadsPageData?.initiatorTypes.value && workloadsPageData?.initiatorTypes.value?.length > 1;

  const containsColumns = (columns: TableColumns, columnsToCheck: string[]) => columns.some((column: any) => columnsToCheck.includes(column.name));

  const addColumnsToColumnsList = (columns: TableColumns, columnsToAdd: string[]) => {
    const newColumns = workloadsConstants.columns.value.filter((column: any) => columnsToAdd.includes(column.name));
    return [...columns, ...newColumns];
  };

  const removeColumnsFromColumnsList = (columns: TableColumns, columnsToRemove: string[]) => columns.filter((column: any) => !columnsToRemove.includes(column.name));

  const handleDynamicColumns = (columns: any) => {
    let modifiedColumns = [...columns];
    if (shouldShowCloudColumns()) {
      if (!containsColumns(modifiedColumns, workloadsConstants.WORKLOADS_CLOUD_COLUMNS)) {
        modifiedColumns = addColumnsToColumnsList(modifiedColumns, workloadsConstants.WORKLOADS_CLOUD_COLUMNS);
      }
    } else {
      modifiedColumns = removeColumnsFromColumnsList(modifiedColumns, workloadsConstants.WORKLOADS_CLOUD_COLUMNS);
    }

    if (shouldShowCacheColumns()) {
      if (!containsColumns(modifiedColumns, workloadsConstants.WORKLOADS_CACHE_COLUMNS)) {
        modifiedColumns = addColumnsToColumnsList(modifiedColumns, workloadsConstants.WORKLOADS_CACHE_COLUMNS);
      }
    } else {
      modifiedColumns = removeColumnsFromColumnsList(modifiedColumns, workloadsConstants.WORKLOADS_CACHE_COLUMNS);
    }

    if (shouldShowInitiatorTypeColumn()) {
      if (!containsColumns(modifiedColumns, [workloadsConstants.COLUMNS_NAMES[OsType.Windows].initiatorType])) {
        modifiedColumns = addColumnsToColumnsList(modifiedColumns, [workloadsConstants.COLUMNS_NAMES[OsType.Windows].initiatorType]);
      }
    } else {
      modifiedColumns = removeColumnsFromColumnsList(modifiedColumns, [workloadsConstants.COLUMNS_NAMES[OsType.Windows].initiatorType]);
    }

    return modifiedColumns;
  };

  return {
    secondsToHoursMinutesAndSeconds,
    handlePageResize,
    convertValueToCurrentUnitSelection,
    handleDynamicColumns,
  };
};
